import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Link as RouterLink } from 'react-router-dom';
import { EarningsSummary } from '../types';
import { fetchDriverEarnings } from '../services/driverWorkspaceService';
import { StatCardsSkeleton, CardRowSkeleton } from '../components/UI/PageSkeleton';

const money = (value: number) => `Rs ${Math.round(value || 0).toLocaleString('en-IN')}`;

const Earnings: React.FC = () => {
  const [summary, setSummary] = useState<EarningsSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDriverEarnings()
      .then(setSummary)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Stack spacing={3} sx={{ maxWidth: 1120, mx: 'auto' }}>
        <StatCardsSkeleton count={4} />
        <CardRowSkeleton cols={2} />
      </Stack>
    );
  }

  if (!summary) {
    return (
      <Paper variant="outlined" sx={{ p: 5, textAlign: 'center' }}>
        <TrendingUpIcon sx={{ fontSize: 52, color: 'rgba(7,24,45,0.18)', mb: 1.5 }} />
        <Typography variant="h6" fontWeight={700} mb={0.75}>Earnings not available</Typography>
        <Typography color="text.secondary" mb={2.5}>We couldn't load your earnings data. Please try refreshing.</Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>Reload</Button>
      </Paper>
    );
  }

  const maxMonth = Math.max(...summary.monthlyTrend.map((item) => item.estimated), 1);

  return (
    <Stack spacing={3} sx={{ maxWidth: 1120, mx: 'auto' }}>
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Typography variant="h5" fontWeight={900}>Earnings performance</Typography>
        <Typography variant="body2" color="text.secondary">
          Estimated earnings are based on prices shared in unlocked ride leads.
        </Typography>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={6} md={3}><Paper variant="outlined" sx={{ p: 2 }}><Typography variant="caption" color="text.secondary">Estimated value</Typography><Typography variant="h4" fontWeight={900}>{money(summary.estimatedTotal)}</Typography></Paper></Grid>
        <Grid item xs={6} md={3}><Paper variant="outlined" sx={{ p: 2 }}><Typography variant="caption" color="text.secondary">Confirmed value</Typography><Typography variant="h4" fontWeight={900}>{money(summary.confirmedTotal)}</Typography></Paper></Grid>
        <Grid item xs={6} md={3}><Paper variant="outlined" sx={{ p: 2 }}><Typography variant="caption" color="text.secondary">Accepted rides</Typography><Typography variant="h4" fontWeight={900}>{summary.acceptedRides}</Typography></Paper></Grid>
        <Grid item xs={6} md={3}><Paper variant="outlined" sx={{ p: 2 }}><Typography variant="caption" color="text.secondary">Win rate</Typography><Typography variant="h4" fontWeight={900}>{summary.winRate}%</Typography></Paper></Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="h6" fontWeight={900}>Monthly trend</Typography>
            <Stack spacing={1.75} mt={2}>
              {summary.monthlyTrend.map((month) => (
                <Box key={month.month}>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" fontWeight={800}>{month.month}</Typography>
                    <Typography variant="body2" color="text.secondary">{money(month.estimated)} · {month.unlocks} unlocks</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={(month.estimated / maxMonth) * 100} sx={{ height: 8, borderRadius: 4 }} />
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="h6" fontWeight={900}>Next action</Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Mark won/lost outcomes in Activity after every unlock. Better feedback improves lead quality and makes earnings estimates useful.
            </Typography>
            <Stack direction="row" spacing={1} mt={2}>
              <Button component={RouterLink} to="/activity" variant="contained" color="secondary">Update outcomes</Button>
              <Button component={RouterLink} to="/app/rides" variant="outlined">Find rides</Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Typography variant="h6" fontWeight={900}>Route performance</Typography>
        <Stack spacing={1.5} mt={2}>
          {summary.routePerformance.length === 0 && <Typography color="text.secondary">No route data yet.</Typography>}
          {summary.routePerformance.map((route) => (
            <Paper key={route.route} variant="outlined" sx={{ p: 1.75, bgcolor: '#fbfcff' }}>
              <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
                <Box>
                  <Typography fontWeight={900}>{route.route}</Typography>
                  <Typography variant="caption" color="text.secondary">{route.unlocks} unlocks · {route.wins} wins</Typography>
                </Box>
                <Typography fontWeight={900}>{money(route.estimated)}</Typography>
              </Box>
            </Paper>
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
};

export default Earnings;
